import { Component, OnInit, computed, inject, input, output, ChangeDetectionStrategy } from '@angular/core';
import { HisTableContextDirective } from '../../../table/directive/his-table-context.directive';
import { HisTableMeta, HisTableMetaDirective } from '../../../table/directive/his-table-meta.directive';
import { HisUiService } from '../../service/base/his-ui.service';
import { HisNotificationService } from '../../service/notifications/his-notification.service';
@Component({
    selector: 'his-rows-per-page',
    templateUrl: './his-rows-per-page.component.html',
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class HisRowsPerPageComponent implements OnInit {
    private readonly notificationService = inject(HisNotificationService);
    private readonly uiService = inject( HisUiService );
    private readonly tableContext = inject( HisTableContextDirective, { optional: true } );
    private readonly tableMeta = inject( HisTableMetaDirective, { optional: true } );

    // Config inputs (may instead be infered through the tableContext)
    /** The default number of rows per page */
    readonly resultRowsPerPage = input<number>();
    /** The current number of rows per page */
    readonly rowsPerPage = input<number>();

    // Table metadata:
    /** The total number of rows in the table, across all pages */
    readonly totalRowCount = input<number>();
    /** The maximum number of rows that can be configured per page (Default 300) */
    readonly maxRows = input<number>( 300 );
    /** The current page number in the table */
    readonly currentPageNumber = input<number>();
    /** The total number of pages in the table */
    readonly totalNumberOfPages = input<number>();

    /** Event emitted when the number of rows per page actually changes */
    readonly rowsPerPageChange = output<number>();

    protected readonly effectiveState = computed( () => {
        const tableMetaData: Partial<HisTableMeta> = this.tableMeta?.data() ?? {};
        const defaultRowsPerPage = this.resultRowsPerPage() || tableMetaData.tableConfig?.defaultTablePageSize || null;
        const rowsPerPage = this.rowsPerPage() || this.resultRowsPerPage() || tableMetaData.rowsPerPage || null;
        const rowsPerPageValue = rowsPerPage ? String( rowsPerPage ) : '10';
        const totalRowCount = this.totalRowCount() || tableMetaData.totalRowCount || 0;
        const currentPageNumber = this.currentPageNumber() || tableMetaData.currentPage || 0;
        const totalNumberOfPages = this.totalNumberOfPages() || tableMetaData.currentNumberOfPages || 0;
        
        return {
            rowsPerPage,
            rowsPerPageValue,
            defaultRowsPerPage,
            totalRowCount,
            currentPageNumber,
            totalNumberOfPages,
        };
    } );

    protected readonly i18nKeyForRecords = computed( () => 'de.his.faces.tags.table.datascroller.numRowsFound' + ( this.totalRowCount() === 1 ? '.record' : '.records' ) );
    private readonly minRows = 1;

    ngOnInit() {
        this.ensureMaxRowsGreaterThanResultRowsPerPage();
    }

    protected async changed( event: Event ) {
        this.notificationService.clear();
        const inputEl = this.inputElement( event );

        console.log( 'his-rows-per-page.component#changed() #changedValue', inputEl.value );
        console.log( 'his-rows-per-page.component#changed() #previousValue', this.effectiveState().rowsPerPage );
        console.log( 'his-rows-per-page.component#changed() #defaultValue', this.effectiveState().defaultRowsPerPage );

        if ( !this.validateInput( inputEl.value ) ) {
            // similar to JSF: keep invalid state, no emit, show error;
            return;
        }
        
        inputEl.value ||= String( this.effectiveState().defaultRowsPerPage || 10 ); // if empty, reset to default value
        const numValue = +inputEl.value; 
        if ( numValue !== this.effectiveState().rowsPerPage ) {
            // only emit on actual change
            if ( this.tableContext ) {
                await this.tableContext.setTableConfigValue( 'tablePageSize', numValue );
            }
            this.rowsPerPageChange.emit( numValue );
        }
    }

    /** HTMLInputElement from the event. */
    private inputElement(event: Event ) {
        return event.target as HTMLInputElement;
    }

    /**
     *Validates the input value.
     *
     *@param input - The input value to be validated.
     *@returns A boolean indicating whether the input value is valid or not.
     */
    private validateInput( inputVal: string ): boolean {
        if ( inputVal === '' ) {
            // according to JSF, empty inputs are valid and fall back to the default value
            return true;
        }
        //convertes the input value to a number
        const checkInput = +inputVal;
        //check that input is an integer
        if ( isNaN( checkInput ) || checkInput % 1 !== 0 ) {
            this.notificationService.error(
                this.uiService.translate( 'de.his.faces.validator.NumberValidator.type', this.errorMsgLabel(), inputVal ),
                this.uiService.translate( 'view.messages.error' ) );
            return false;
        }
        //returns a error message if the input is smaller than this.minRows or bigger than this.maxRows
        if ( !( checkInput >= this.minRows ) || checkInput > this.maxRows() ) {
            this.notificationService.error(
                this.uiService.translate( 'de.his.faces.validator.NumberValidator.range', this.errorMsgLabel(), this.minRows, this.maxRows() ),
                this.uiService.translate( 'view.messages.error' ) );
            return false;
        }
        return true;
    }

    private errorMsgLabel() {
        return this.uiService.translate( 'de.his.faces.tags.table.datascroller.numRowsPerPage' );
    }

    // ist das überhaupt noch notwendig?
    // in welchen Fällen kann das auftreten?
    private ensureMaxRowsGreaterThanResultRowsPerPage() {
        if ( this.effectiveState().rowsPerPage > this.maxRows() ) {
            this.notificationService.error(
                this.uiService.translate(
                    'his-search-result.component - ' +
                    'ungültige Parameterübergabe: resultRowsPerPage (' +
                    this.effectiveState().rowsPerPage + ') darf nicht größer sein als maxRows (' +
                    this.maxRows() + '). Bitte ändern Sie die Parameter.'),
                this.uiService.translate( 'view.messages.error' )
            );
            throw new Error(
                'his-search-result.component - ' +
                'passed invalid parameters: resultRowsPerPage ' +
                this.effectiveState().rowsPerPage + ' must not be > than maxRows ' +
                this.maxRows() + '. Please adjust parameters.'
            );
        }
    }

}
